"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

// Where the page's sticky-header stack starts from the viewport top:
//   - Dashboard: the tabs band pins directly at 64 (subHeader={2} noPageHeader
//     in sticky-header.tsx `topValues`) and there is no page-header band above
//     it inside the content area, so `pageHeaderHeight` stays 0.
//   - Account page: the currency-dropdown band pins at 63 (subHeader={1}) and
//     the tabs band must pin directly BELOW it — the account layout passes
//     baseOffset=63 and reports the dropdown band's measured height via
//     `setPageHeaderHeight`, so `tabsTop = 63 + measured band height`. This
//     replaced the hand-estimated 94px pin-top, which drifted from the real
//     band bottom (~105px mobile / ~109px desktop) and painted the pinned
//     ledger header under the opaque dropdown band on mobile.
const DASHBOARD_TABS_PIN_TOP = 64;

// Seed values used only for the first frame, before the ResizeObservers report
// real measurements. Kept slightly generous so the worst first-frame case is a
// small gap (never a header pinned *under* the band, which would hide it).
const DEFAULT_TABS_HEIGHT = 33; // desktop <hr> band (hr + lg:my-4 top margin)
const DEFAULT_LIST_HEADER_HEIGHT = 106; // title row + filter row

interface DashboardHeaderContextValue {
  // Live band heights reported by the headers (via useMeasuredHeight).
  // The page header (the account layout's currency-dropdown band) reports the
  // height of its sticky band; the tabs header (DashboardTabsHeader /
  // AccountActivityTabs) reports the height of its sticky band; the active
  // list header (LedgerEntryHeader or a payments header) reports its own.
  setPageHeaderHeight: (px: number) => void;
  setTabsHeight: (px: number) => void;
  setListHeaderHeight: (px: number) => void;

  // Calculated offsets (used by child components for sticky `top`)
  tabsTop: number;
  listHeaderTop: number;
  activityDateGroupTop: number;
  paymentsDateGroupTop: number;
}

const DashboardHeaderContext = createContext<
  DashboardHeaderContextValue | undefined
>(undefined);

interface DashboardHeaderProviderProps extends WithChildren {
  /** Viewport `top` (px) where the page's first in-content sticky band pins.
   * Dashboard: 64 (the tabs band itself). Account: 63 (the currency-dropdown
   * band; the tabs band then pins below it via the measured band height). */
  baseOffset?: number;
  /** First-frame seed for the page-header band height, used until its
   * ResizeObserver reports. Pass the tallest known band height so the worst
   * first-frame case is a small gap, never an overlap. Dashboard: 0 (none). */
  initialPageHeaderHeight?: number;
}

export function DashboardHeaderProvider({
  children,
  baseOffset = DASHBOARD_TABS_PIN_TOP,
  initialPageHeaderHeight = 0,
}: DashboardHeaderProviderProps) {
  // Measured heights. Numeric state with an identity bail-out so re-measuring
  // to the same integer px never re-renders the dashboard tree (the headers'
  // ResizeObservers fire on every scroll-driven reflow on some browsers).
  const [pageHeaderHeight, setPageHeaderHeightState] = useState(
    initialPageHeaderHeight
  );
  const [tabsHeight, setTabsHeightState] = useState(DEFAULT_TABS_HEIGHT);
  const [listHeaderHeight, setListHeaderHeightState] = useState(
    DEFAULT_LIST_HEADER_HEIGHT
  );

  const setPageHeaderHeight = useCallback((px: number) => {
    setPageHeaderHeightState((prev) => (prev === px ? prev : px));
  }, []);

  const setTabsHeight = useCallback((px: number) => {
    setTabsHeightState((prev) => (prev === px ? prev : px));
  }, []);

  const setListHeaderHeight = useCallback((px: number) => {
    setListHeaderHeightState((prev) => (prev === px ? prev : px));
  }, []);

  const offsets = useMemo(() => {
    // The tabs band pins directly below the page-header band (if any):
    // base pin-top + measured page-header band height.
    const tabsTop = baseOffset + pageHeaderHeight;

    // The list header (LedgerEntryHeader / payments header) pins directly below
    // the tabs band: tabs pin-top + measured band height.
    const listHeaderTop = tabsTop + tabsHeight;

    // Date-group headers pin directly below the list header. Only one list is
    // mounted at a time, so the single measured list-header height is correct
    // for whichever (activity or payments) is active.
    const dateGroupTop = listHeaderTop + listHeaderHeight;

    return {
      tabsTop,
      listHeaderTop,
      activityDateGroupTop: dateGroupTop,
      paymentsDateGroupTop: dateGroupTop,
    };
  }, [baseOffset, pageHeaderHeight, tabsHeight, listHeaderHeight]);

  const value = useMemo(
    () => ({
      setPageHeaderHeight,
      setTabsHeight,
      setListHeaderHeight,
      tabsTop: offsets.tabsTop,
      listHeaderTop: offsets.listHeaderTop,
      activityDateGroupTop: offsets.activityDateGroupTop,
      paymentsDateGroupTop: offsets.paymentsDateGroupTop,
    }),
    [setPageHeaderHeight, setTabsHeight, setListHeaderHeight, offsets]
  );

  return (
    <DashboardHeaderContext.Provider value={value}>
      {children}
    </DashboardHeaderContext.Provider>
  );
}

export function useDashboardHeader(): DashboardHeaderContextValue {
  const context = useContext(DashboardHeaderContext);
  if (!context) {
    // Defaults when used outside a provider (e.g. non-dashboard pages). These
    // mirror the seed values so a stray consumer still gets sane offsets.
    return {
      setPageHeaderHeight: () => {},
      setTabsHeight: () => {},
      setListHeaderHeight: () => {},
      tabsTop: DASHBOARD_TABS_PIN_TOP,
      listHeaderTop: DASHBOARD_TABS_PIN_TOP + DEFAULT_TABS_HEIGHT,
      activityDateGroupTop:
        DASHBOARD_TABS_PIN_TOP +
        DEFAULT_TABS_HEIGHT +
        DEFAULT_LIST_HEADER_HEIGHT,
      paymentsDateGroupTop:
        DASHBOARD_TABS_PIN_TOP +
        DEFAULT_TABS_HEIGHT +
        DEFAULT_LIST_HEADER_HEIGHT,
    };
  }
  return context;
}
